import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { PRODUCTS } from "../mock";
import { useAllProducts } from "../hooks/useProducts";
import { Tag, ChevronRight, Recycle, ShieldCheck, Wrench, Percent } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "../components/ui/button";

// Sub-sections shown as tabs
const SUB_SECTIONS = [
  { id: "all",                label: "Tout voir",       icon: "LayoutGrid" },
  { id: "refrigerateur",      label: "Réfrigérateurs",  icon: "Refrigerator" },
  { id: "lave-linge",         label: "Lave-linge",      icon: "WashingMachine" },
  { id: "seche-linge",        label: "Sèche-linge",     icon: "Wind" },
  { id: "tv-video",           label: "TV & Image",      icon: "Tv" },
  { id: "cuisson",            label: "Cuisson & Four",  icon: "CookingPot" },
  { id: "lave-vaisselle",     label: "Lave-vaisselle",  icon: "UtensilsCrossed" },
  { id: "son",                label: "Son & Hi-fi",     icon: "Speaker" },
  { id: "petit-electromenager", label: "Petit électroménager", icon: "Coffee" },
];

const Occasion = () => {
  const [active, setActive] = useState("all");
  const allProducts = useAllProducts();

  const occasionProducts = useMemo(
    () => allProducts.filter((p) => p.condition === "Occasion"),
    [allProducts]
  );

  const filteredProducts = useMemo(() => {
    if (active === "all") return occasionProducts;
    return occasionProducts.filter((p) => p.category === active);
  }, [active, occasionProducts]);

  const countByCat = useMemo(() => {
    const c = { all: occasionProducts.length };
    for (const p of occasionProducts) {
      c[p.category] = (c[p.category] || 0) + 1;
    }
    return c;
  }, [occasionProducts]);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center text-sm text-neutral-600 gap-1">
          <Link to="/" className="hover:text-[#0b2960]">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#0b2960] font-semibold">Occasion garantie</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#0b2960] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #e11d24 0, transparent 40%), radial-gradient(circle at 80% 70%, #ffffff 0, transparent 30%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-14 relative">
          <div className="inline-flex items-center gap-2 bg-[#e11d24] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4">
            <Recycle className="h-3.5 w-3.5" /> Occasion reconditionnée · jusqu'à -70%
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight tracking-tight">
            Notre sélection <span className="text-[#ff5a5f]">d'occasion</span>
          </h1>
          <p className="mt-3 text-neutral-200 max-w-2xl">
            Tous nos appareils d'occasion sont testés, révisés et garantis 4 mois. Explorez par catégorie ci-dessous.
          </p>
          <div className="mt-5 flex flex-wrap gap-6 text-sm text-neutral-200">
            <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-[#ff5a5f]" /> Reconditionné en atelier</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#ff5a5f]" /> Garantie 4 mois</div>
            <div className="flex items-center gap-2"><Percent className="h-4 w-4 text-[#ff5a5f]" /> Prix imbattables</div>
          </div>
        </div>
      </section>

      {/* Sub-section tabs */}
      <div className="bg-white border-b border-neutral-200 sticky top-[128px] md:top-[152px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-4 px-4 scrollbar-hide">
            {SUB_SECTIONS.map((s) => {
              const Icon = LucideIcons[s.icon] || Tag;
              const count = countByCat[s.id] || 0;
              const isActive = active === s.id;
              const disabled = s.id !== "all" && count === 0;
              return (
                <button
                  key={s.id}
                  disabled={disabled}
                  onClick={() => setActive(s.id)}
                  className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap border transition-all ${
                    isActive
                      ? "bg-[#0b2960] text-white border-[#0b2960]"
                      : disabled
                        ? "border-neutral-200 text-neutral-300 cursor-not-allowed"
                        : "border-neutral-200 text-neutral-700 hover:border-[#0b2960] hover:text-[#0b2960]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{s.label}</span>
                  <span className={`ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white text-[#0b2960]" : "bg-neutral-100 text-neutral-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products grid */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Occasion garantie</div>
            <h2 className="text-xl md:text-2xl font-extrabold text-[#0b2960]">
              {active === "all" ? "Toutes les occasions" : SUB_SECTIONS.find((s) => s.id === active)?.label}
            </h2>
            <p className="text-sm text-neutral-500 mt-1">{filteredProducts.length} produit{filteredProducts.length > 1 ? "s" : ""} disponible{filteredProducts.length > 1 ? "s" : ""}</p>
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-12 text-center">
            <p className="text-neutral-600">Aucun produit dans cette sous-catégorie pour le moment.</p>
            <Button onClick={() => setActive("all")} className="mt-4 bg-[#0b2960] hover:bg-[#081e47]">Voir toutes les occasions</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default Occasion;
